import { useState } from "react";
import { SignOutButton } from "@clerk/react";
import { ShieldCheck, Download, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { BUSINESS_TYPES } from "@/lib/dates";
import { PageHeader, Panel, RoleBadge } from "@/components/common";

const useSaver = () => {
  const { reload } = useMember();
  return async (fn) => {
    try {
      await fn();
      await reload();
      toast.success("Opgeslagen");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
};

const ProfileCard = () => {
  const api = useApi();
  const save = useSaver();
  const { member } = useMember();
  const [f, setF] = useState({ name: member.name, phone: member.phone || "", email_schedule: member.email_schedule !== false });
  return (
    <Panel data-testid="profile-card">
      <div className="flex items-center justify-between"><h2 className="font-semibold text-lg">Mijn profiel</h2><RoleBadge role={member.role} /></div>
      <p className="text-sm text-slate-500">{member.email}</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div><Label htmlFor="p-name">Naam</Label><Input id="p-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="profile-name-input" /></div>
        <div><Label htmlFor="p-phone">Telefoon</Label><Input id="p-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" data-testid="profile-phone-input" /></div>
      </div>
      <label className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 p-3 text-sm">
        <span><b>Roostermail ontvangen</b><span className="block text-xs text-slate-500">Krijg je diensten per e-mail zodra het weekrooster klaarstaat.</span></span>
        <Switch checked={f.email_schedule} onCheckedChange={(v) => setF({ ...f, email_schedule: v })} data-testid="profile-email-schedule-switch" />
      </label>
      <Button className="mt-4" onClick={() => save(() => api.patch("/me", f))} data-testid="profile-save-button">Opslaan</Button>
    </Panel>
  );
};

const BusinessCard = () => {
  const api = useApi();
  const save = useSaver();
  const { business } = useMember();
  const [f, setF] = useState({ name: business.name, business_type: business.business_type });
  return (
    <Panel data-testid="business-card">
      <h2 className="font-semibold text-lg">Bedrijfsgegevens</h2>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div><Label htmlFor="b-name">Bedrijfsnaam</Label><Input id="b-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="business-name-input" /></div>
        <div>
          <Label>Soort zaak</Label>
          <Select value={f.business_type} onValueChange={(v) => setF({ ...f, business_type: v })}>
            <SelectTrigger className="mt-1.5" data-testid="business-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(BUSINESS_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button className="mt-4" onClick={() => save(() => api.patch("/business", f))} data-testid="business-save-button">Opslaan</Button>
    </Panel>
  );
};

const PrivacyCard = () => {
  const api = useApi();
  const download = async () => {
    try {
      const { data } = await api.get("/privacy/my-data");
      downloadBlob(JSON.stringify(data, null, 2), "mijn-gegevens-shiftmate.json", "application/json");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel data-testid="privacy-card">
      <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#0D5C46]" /><h2 className="font-semibold text-lg">Privacy (AVG)</h2></div>
      <ul className="mt-3 space-y-1.5 text-sm text-slate-600 list-disc pl-5">
        <li>We bewaren alleen wat nodig is: naam, e-mail, telefoon, rooster en gewerkte uren.</li>
        <li>Collega's zien alleen je naam en rol. Uurloon en contactgegevens zijn alleen zichtbaar voor eigenaar en manager.</li>
        <li>Gegevens van jouw bedrijf zijn volledig gescheiden van andere bedrijven.</li>
        <li>Je kunt altijd een kopie van je eigen gegevens downloaden (recht op inzage).</li>
      </ul>
      <a href="/privacy" className="block mt-3 text-sm font-medium text-[#0D5C46] underline" data-testid="settings-privacy-link">Lees de volledige privacyverklaring</a>
      <Button variant="outline" className="mt-4" onClick={download} data-testid="privacy-download-button"><Download className="h-4 w-4 mr-2" />Download mijn gegevens</Button>
    </Panel>
  );
};

export default function SettingsPage() {
  const { isOwner } = useMember();
  return (
    <div data-testid="settings-page">
      <PageHeader title="Instellingen" subtitle="Profiel, bedrijf en privacy." />
      <div className="space-y-4">
        <ProfileCard />
        {isOwner && <BusinessCard />}
        <PrivacyCard />
        <SignOutButton><Button variant="ghost" className="text-red-600" data-testid="settings-signout-button"><LogOut className="h-4 w-4 mr-2" />Uitloggen</Button></SignOutButton>
      </div>
    </div>
  );
}
